// "Try its branch": the session's own app, running in its sandbox on its branch, in
// a new tab with the agent's guide and the feedback layer (one-tap verdicts, a box,
// point at something), whose word goes straight back to the agent (agent-factory
// README "Previews"; docs/factory.md). The tab says it is starting until it is up.
import { useState } from 'react'
import { api } from '@/runtime'
import { Button } from '@/components/ui'
import type { AgentRunResource } from '@/kinds/agentrun/apply'

/** Whether a session can show its app: a live coding agent (Prime has no app of its own). */
export const canPreview = (run: AgentRunResource) =>
  !!run.spec.name &&
  run.spec.template !== 'prime' &&
  run.spec.status !== 'Ended' &&
  run.spec.status !== 'Failed'

/**
 * Open a session's app in a new tab. Call it inside the tap (or key press): the tab is
 * opened at once, or the browser blocks it once we have waited, and it never gets a
 * handle back on this page. Throws, with the tab closed, when it did not start.
 */
export const openPreview = async (name: string, to?: string) => {
  const tab = window.open('', '_blank')
  if (tab) tab.opener = null
  try {
    const r = await api.factoryPreview(name, to)
    if (tab) tab.location.href = r.url
    else window.location.href = r.url
  } catch (e) {
    tab?.close()
    throw e
  }
}

export const PreviewButton = ({ run, to }: { run: AgentRunResource; to?: string }) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  if (!canPreview(run)) return null
  const open = async () => {
    setBusy(true)
    setError('')
    try {
      await openPreview(run.spec.name ?? '', to)
    } catch (e) {
      setError((e as Error).message || 'It did not start')
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <Button variant="chip" onClick={() => void open()} disabled={busy} data-testid="run-preview">
        {busy ? 'Starting…' : 'Try its branch'}
      </Button>
      {error && (
        <span className="text-xs text-muted self-center" data-testid="run-preview-error">
          {error}
        </span>
      )}
    </>
  )
}

/**
 * A project's "Try it": what is on main, in a session kept for trying it (one
 * per project, started the first time; it changes nothing), opened like "Spin
 * up the app". It is how a prototype is tried once its agents have ended.
 */
export const TryButton = ({ projectId }: { projectId: string }) => {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const open = async () => {
    // opened inside the tap, as above, or the browser blocks it once we have waited
    const tab = window.open('', '_blank')
    if (tab) tab.opener = null
    setBusy(true)
    setError('')
    try {
      const r = await api.factoryTry(projectId)
      if (tab) tab.location.href = r.url
      else window.location.href = r.url
    } catch (e) {
      tab?.close()
      setError((e as Error).message || 'It did not start')
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <Button variant="chip" onClick={() => void open()} disabled={busy} data-testid="project-try">
        {busy ? 'Starting…' : 'Try it'}
      </Button>
      {error && (
        <span className="text-xs text-muted self-center" data-testid="project-try-error">
          {error}
        </span>
      )}
    </>
  )
}
